import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { extend } from 'lodash'
import api from 'common/api'

/** important: this is longitude followed by latitude */
type GEOPointCoordinates = [number, number]
type APICollege = {
  Scid: number
  Name?: string
  City?: string
  State?: string
  Country: string
  Location?: {
    type: 'Point'
    coordinates: GEOPointCoordinates
  }
  HasDynamicContent: boolean
  Integrations?: {
    DocumentDelegateScid?: number
    DocumentDelegateName?: string
  }
  Features?: {
    SupportsContentManagement: boolean
    SupportsStudentOutreach: boolean
    SupportsStudentAnalytics: boolean
    SupportsUserUploadedSegments: boolean
    SupportsCRMMatchReport: boolean
    ApplicationIntegration: boolean
    ApplicationActivation: boolean
    CrmIntegration: boolean
    ExpandedSlateSegmentation: boolean
    BypassSlateAppRoundVerification: boolean
    SupportsPremiumHighSchoolExplorer: boolean
  }
}

type State = {
  record?: APICollege
}

type SliceState = {
  college: State
}

export const isFeatureEnabled = (store: SliceState, name: keyof NonNullable<APICollege['Features']>) =>
  store.college.record?.Features?.[name] || false
export const selectFeature = (feature: Parameters<typeof isFeatureEnabled>[1]) => (store: SliceState) =>
  isFeatureEnabled(store, feature)
export const selectFeatures = (store: SliceState) => store.college.record?.Features
export const selectScid = (store: SliceState) => store.college.record?.Scid
export const selectName = (store: SliceState) => store.college.record?.Name
export const selectState = (store: SliceState) => store.college.record?.State
export const isInUnitedStates = (store: SliceState) => store.college.record?.Country === 'United States'
export const selectDocumentDelegateName = (store: SliceState) =>
  store.college.record?.Integrations?.DocumentDelegateName || ''
export const selectHasDynamicContent = (store: SliceState) => store.college.record?.HasDynamicContent || false

const fetchCollege = createAsyncThunk('colleges/fetch', async (scid: number) => {
  return api.get<APICollege>(`/api/university/${scid}`)
})

const INITIAL_STATE: State = {}
const slice = createSlice({
  name: 'college',
  initialState: INITIAL_STATE,
  reducers: {
    updateCollege(state, action) {
      extend(state.record, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCollege.fulfilled, (state, action) => {
      state.record = action.payload
    })
  },
})

export default slice.reducer

export const actions = {
  ...slice.actions,
  fetchCollege,
}
